import React, { FC, useCallback } from "react";
import { connect } from "react-redux";

import { faUsers } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";

import Resource from "@mapmycustomers/shared/types/entity/Resource";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { isDefined, isNotEmpty } from "@mapmycustomers/shared/util/assert";
import { formatFileSize } from "@mapmycustomers/shared/util/formatters";
import { ListWithOverflowItem } from "@mapmycustomers/ui";

import { getTeamsOfEntireOrganization, getUsersOfEntireOrganization } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { formatRawDate } from "@app/util/formatters";

import getSharedWithLabel from "../utils/getSharedWithLabel";

import styles from "./ResourceInfo.module.scss";

export interface ResourceInfoProps {
  resource: Resource;
  teams: Team[];
  users: User[];
}

const ResourceInfo: FC<ResourceInfoProps> = ({ resource, teams, users }) => {
  const intl = useIntl();
  const sharedWithLabel = getSharedWithLabel(intl, resource, users, teams);

  const sharedWithOverflowRenderer = useCallback(
    (count: number) => {
      return (
        <div className={styles.fullWidth}>
          {intl.formatMessage(
            {
              id: "resources.list.resource.sharedWith.overflowRender",
              defaultMessage: "+ {count} others",
              description: "overflow Render for shared with text",
            },
            { count }
          )}
        </div>
      );
    },
    [intl]
  );

  return (
    <div className={styles.info}>
      <span className={styles.name}>{resource.name}</span>
      <div className={styles.subText}>
        <div className={styles.fullWidth}>
          {[
            formatRawDate(resource.updatedAt, "PP"),
            isNotEmpty(resource.file?.contentLength)
              ? formatFileSize(resource.file.contentLength, 1000)
              : undefined,
          ]
            .filter(isDefined)
            .join(" – ")}
        </div>
        {sharedWithLabel.length > 0 && (
          <div className={styles.sharedWith}>
            {" – "}
            <FontAwesomeIcon icon={faUsers} />
            <ListWithOverflowItem
              className={styles.overflow}
              overflowRender={sharedWithOverflowRenderer}
            >
              {sharedWithLabel}
            </ListWithOverflowItem>
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  teams: getTeamsOfEntireOrganization(state),
  users: getUsersOfEntireOrganization(state),
});

export default connect(mapStateToProps)(ResourceInfo);
